import type {
  CrmContactInteractionTimelineItem,
  CrmContactInteractionTone,
} from "@ease/crm/contact-interaction-timeline";
import {
  getCrmActionHrefKind,
  normalizeCrmActionHref,
  shouldOpenCrmActionHrefInNewTab,
} from "@ease/crm/safe-action-hrefs";
import { ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/i18n";

interface CrmInteractionTimelineCardProps {
  items: CrmContactInteractionTimelineItem[];
  title?: string;
  description?: string;
  className?: string;
}

export function CrmInteractionTimelineCard({
  items,
  title = "Interaction highlights",
  description = "Recent configured touchpoints from this contact's product, service, commerce, and support history.",
  className,
}: CrmInteractionTimelineCardProps) {
  if (items.length === 0) return null;

  return (
    <section className={cn("rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4", className)}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">{title}</h3>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{description}</p>
        </div>
        <span className="shrink-0 rounded-full border border-[var(--color-border)] px-2.5 py-1 text-xs font-medium text-[var(--color-muted-foreground)]">
          {items.length}
        </span>
      </div>
      <div className="mt-4 space-y-3">
        {items.map((item) => (
          <div key={item.key} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-3">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[var(--color-foreground)]">{item.label}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {item.channel ? <InteractionChip label={item.channel} /> : null}
                  {item.outcome ? <InteractionChip label={item.outcome} tone={item.tone} /> : null}
                </div>
              </div>
              {item.occurredAt ? (
                <span className="shrink-0 text-xs text-[var(--color-muted-foreground)]">
                  {formatDate(item.occurredAt)}
                </span>
              ) : null}
            </div>
            {item.description ? (
              <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">{item.description}</p>
            ) : null}
            {item.href ? <InteractionActionLink item={item} /> : null}
          </div>
        ))}
      </div>
    </section>
  );
}

function InteractionChip({
  label,
  tone = "neutral",
}: {
  label: string;
  tone?: CrmContactInteractionTone;
}) {
  return (
    <span className={cn(
      "rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize",
      getCrmInteractionToneClasses(tone),
    )}>
      {label}
    </span>
  );
}

function InteractionActionLink({ item }: { item: CrmContactInteractionTimelineItem }) {
  if (!item.href) return null;
  const href = normalizeCrmActionHref(item.href);
  if (!href) return null;

  const hrefKind = getCrmActionHrefKind(href);
  if (!hrefKind) return null;

  const label = item.actionLabel ?? "Open";
  const openInNewTab = shouldOpenCrmActionHrefInNewTab(href);

  return (
    <a
      href={href}
      target={openInNewTab ? "_blank" : undefined}
      rel={openInNewTab ? "noreferrer" : undefined}
      className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] px-2.5 py-1 text-xs font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-muted)]"
    >
      <span>{label}</span>
      <ExternalLink className="h-3.5 w-3.5" />
    </a>
  );
}

export function getCrmInteractionToneClasses(tone: CrmContactInteractionTone) {
  if (tone === "success") {
    return "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]";
  }
  if (tone === "warning") {
    return "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
  }
  if (tone === "danger") {
    return "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }
  if (tone === "info") {
    return "border-[var(--color-info)]/30 bg-[var(--color-info)]/10 text-[var(--color-info)]";
  }
  return "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)]";
}
